import { colord, extend } from "colord";
import a11yPlugin from "colord/plugins/a11y";
import lchPlugin from "colord/plugins/lch";
import mixPlugin from "colord/plugins/mix";

extend([a11yPlugin, lchPlugin, mixPlugin]);

const READABLE_TEXT_ON_LIGHT_BASE = "#111";
const MUTED_TEXT_ON_LIGHT_BASE = "#666768";
const DISABLED_TEXT_ON_LIGHT_BASE = "#999";

const READABLE_TEXT_ON_DARK_BASE = "#FFF";
const MUTED_TEXT_ON_DARK_BASE = "#EEE";
const DISABLED_TEXT_ON_DARK_BASE = "#D0D0D0";

const DISABLED_BACKGROUND_BASE = "#666";

export const getTintedReadableColor = (color: string) => {
  const isLight = isColorLightForText(color);

  if (isLight) {
    return colord(READABLE_TEXT_ON_LIGHT_BASE).mix(color, 0.01).toHex();
  } else {
    return colord(READABLE_TEXT_ON_DARK_BASE).mix(color, 0.01).toHex();
  }
};

export const getTintedReadableMutedColor = (color: string) => {
  const isLight = isColorLightForText(color);

  if (isLight) {
    return colord(MUTED_TEXT_ON_LIGHT_BASE).mix(color, 0.15).toHex();
  } else {
    return colord(MUTED_TEXT_ON_DARK_BASE).mix(color, 0.15).toHex();
  }
};

export const getTintedReadableDisabledColor = (color: string) => {
  const isLight = isColorLightForText(color);

  if (isLight) {
    return colord(DISABLED_TEXT_ON_LIGHT_BASE).mix(color, 0.25).toHex();
  } else {
    return colord(DISABLED_TEXT_ON_DARK_BASE).mix(color, 0.25).toHex();
  }
};

export const getTintedBackgroundColorVeryLight = (color: string) => {
  if (isColorLight(color)) {
    return colord(color).mix("#000", 0.08).toHex();
  } else {
    return colord(color).mix("#FFF", 0.08).toHex();
  }
};

export const getTintedBorderColor = (color: string) => {
  return getTintedReadableMutedColor(color);
};

export const getTintedBorderColorActive = (color: string) => {
  return getTintedReadableColor(color);
};

export const getTintedBorderColorDisabled = (color: string) => {
  if (isColorLight(color)) {
    return colord(color).mix("#666", 0.2).toHex();
  } else {
    return colord(color).mix("#666", 0.2).toHex();
  }
};

export const getTintedBackgroundColorDisabled = (color: string) => {
  return colord(color).mix(DISABLED_BACKGROUND_BASE, 0.6).toHex();
};

/**
 * Checks whether a color is light, using the LCH colorspace. This produces
 * better results than HSL lightness.
 */
const isColorLight = (color: string) => colord(color).toLch().l > 50;

/**
 * Checks whether a color is light, using the LCH colorspace. This produces
 * better results than HSL lightness.
 *
 * For text, use a different threshold (55 rather than 50). This keeps the text
 * light in some borderline cases.
 */
const isColorLightForText = (color: string) => colord(color).toLch().l > 55;
